import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NONE = '_none';

export const Pick = ({ label, value, options, onChange, testId, disabled, allowNone = true }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Choose…" /></SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>—</SelectItem>}
        {options.map((o) => <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const DETAIL_KEYS = ['date', 'status', 'ncworks', 'myhelp', 'snap_closure', 'qa_status'];

export const ContactEditDialog = ({ target, options, me, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [orig, setOrig] = useState({});
  const [saving, setSaving] = useState(false);
  const n = target?.n;
  const existing = target ? (target.record.contacts || []).find((x) => x.n === n) : null;

  useEffect(() => {
    if (!target) return;
    const c = (target.record.contacts || []).find((x) => x.n === n) || {};
    const f = { assigned_user_id: c.assigned_user_id || null, date: c.date && /^\d{4}-\d{2}-\d{2}$/.test(c.date) ? c.date : '', status: c.status || null,
      ncworks: c.ncworks || null, myhelp: c.myhelp || null, snap_closure: c.snap_closure || null, qa_status: c.qa_status || null };
    setForm(f);
    setOrig(f);
  }, [target, n]);

  if (!target) return null;
  const isNew4 = n === 4 && !existing;
  const canReassign = options.is_manager;
  const canRecord = !isNew4 && (options.is_manager || existing?.assigned_user_id === me);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const staff = options.staff.map((s) => ({ value: s.id, label: s.name }));
  const title = n === 1 ? 'Initial Contact' : `Contact ${n}`;
  const legacyName = existing && !existing.assigned_user_id ? existing.assigned_user_name : null;

  const save = async () => {
    const body = {};
    if (form.assigned_user_id !== orig.assigned_user_id) body.assigned_user_id = form.assigned_user_id;
    if (canRecord) DETAIL_KEYS.forEach((k) => { if (form[k] !== orig[k]) body[k] = form[k] || null; });
    if (isNew4 && !body.assigned_user_id) return toast.error('Choose who Contact 4 is assigned to');
    if (!Object.keys(body).length) return onClose();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/referral-tracker/records/${target.record.id}/contacts/${n}`, body);
      toast.success(isNew4 ? 'Contact 4 added and task created' : `${title} saved`);
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rt-contact-dialog">
        <DialogHeader>
          <DialogTitle>{isNew4 ? 'Add Contact 4' : title}: {target.record.first_name} {target.record.last_name}</DialogTitle>
          <DialogDescription>
            {legacyName ? `From the spreadsheet: ${legacyName}. ` : ''}
            {canReassign ? 'Assigning someone creates a task for them.' : 'Only admins, managers and supervisors can reassign.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Pick label="Assigned User" value={form.assigned_user_id} options={staff} onChange={set('assigned_user_id')} disabled={!canReassign} testId="rt-contact-user" />
          {!isNew4 && (
            <>
              <div className="space-y-1.5">
                <Label>Contact Date</Label>
                <Input type="date" value={form.date || ''} onChange={(e) => set('date')(e.target.value)} disabled={!canRecord} data-testid="rt-contact-date" />
              </div>
              <div className="col-span-2">
                <Pick label={n === 1 ? 'Status' : 'Outcome'} value={form.status} options={n === 1 ? options.initial_statuses : options.outreach_statuses} onChange={set('status')} disabled={!canRecord} testId="rt-contact-status" />
                {options.terminal_outcomes.includes(form.status) && n < 4 && <p className="text-xs text-amber-700 mt-1" data-testid="rt-ends-outreach-note">This outcome ends outreach. Later contacts become unavailable.</p>}
                {form.status === 'Enrolled' && orig.status !== 'Enrolled' && <p className="text-xs text-green-700 mt-1">Saving adds the SNAP E&amp;T Program enrollment for this tab if the participant doesn't have it yet.</p>}
              </div>
              <Pick label="NCWorks" value={form.ncworks} options={options.yes_no} onChange={set('ncworks')} disabled={!canRecord} testId="rt-contact-ncworks" />
              <Pick label="MyHELP" value={form.myhelp} options={options.yes_no} onChange={set('myhelp')} disabled={!canRecord} testId="rt-contact-myhelp" />
              <Pick label="SNAP Closure" value={form.snap_closure} options={options.yes_no} onChange={set('snap_closure')} disabled={!canRecord} testId="rt-contact-snap-closure" />
              <Pick label="QA Status" value={form.qa_status} options={options.qa} onChange={set('qa_status')} disabled={!canRecord} testId="rt-contact-qa" />
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rt-contact-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || (!canRecord && !canReassign)} data-testid="rt-contact-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
